import * as XLSX from 'xlsx/xlsx.mjs';

/*
  Pipeline for importing lançamentos (Saída / Entrada / Transferência) from a
  spreadsheet in the standard Vita layout:
  Tipo | Status | Competência | Vencimento | Conta | Categoria | Descrição | Conta2 | Juros | Multa | Desconto | Valor

  Transfer rows carry BOTH accounts involved (Conta = origem/primária, Conta2 = a outra
  conta do movimento) directly in the same row, so each row is self-sufficient to build
  the two journal-entry legs of a transfer — no cross-row matching/pairing is needed.

  This module has no React dependency so it can be exercised independently of
  the modal that drives it (ImportSpreadsheetModal.js).
*/

const FIXED_COLUMN_ORDER = [
  'tipo',
  'status',
  'competencia',
  'vencimento',
  'conta',
  'categoria',
  'descricao',
  'conta2',
  'juros',
  'multa',
  'desconto',
  'valor',
];

const COLUMN_ALIASES = {
  tipo: ['tipo'],
  status: ['status'],
  competencia: ['competencia', 'data de competencia', 'data competencia'],
  vencimento: ['vencimento', 'data de vencimento', 'data vencimento'],
  conta: ['conta'],
  categoria: ['categoria'],
  descricao: ['descricao'],
  conta2: ['conta2', 'conta 2'],
  juros: ['juros'],
  multa: ['multa'],
  desconto: ['desconto'],
  valor: ['valor'],
};

const TIPO_MAP = {
  saida: { journalEntryType: 1, isInput: false, isTransferLeg: false },
  entrada: { journalEntryType: 1, isInput: true, isTransferLeg: false },
  'transferencia (saida)': { journalEntryType: 5, isInput: false, isTransferLeg: true },
  'transferencia (entrada)': { journalEntryType: 5, isInput: true, isTransferLeg: true },
};

const KNOWN_UNPAID_STATUS = ['vencido', 'pendente', 'a vencer', ''];

export function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

function padNumber(n) {
  return String(n).padStart(2, '0');
}

function formatISODate(year, month, day) {
  return `${year}-${padNumber(month)}-${padNumber(day)}`;
}

export function parseDateBR(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatISODate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    const brMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (brMatch) {
      const [, day, month, year] = brMatch;
      return formatISODate(Number(year), Number(month), Number(day));
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return formatISODate(Number(year), Number(month), Number(day));
    }
  }

  return null;
}

export function parseCurrencyBR(value) {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;

  let str = String(value).trim();
  if (str === '') return 0;

  str = str.replace(/^r\$\s*/i, '').trim();
  if (str === '' || str === '-') return 0;

  const isNegative = str.startsWith('-');
  if (isNegative) str = str.slice(1).trim();

  str = str.replace(/\./g, '').replace(',', '.');
  const num = Number(str);
  if (Number.isNaN(num)) return NaN;

  return isNegative ? -num : num;
}

function isRowEmpty(rawRow) {
  return rawRow.every((cell) => cell === undefined || cell === null || String(cell).trim() === '');
}

export function buildColumnMap(headers) {
  const normalizedHeaders = headers.map(normalizeText);
  const map = {};

  Object.entries(COLUMN_ALIASES).forEach(([key, aliases]) => {
    const idx = normalizedHeaders.findIndex((h) => aliases.includes(h));
    map[key] = idx;
  });

  FIXED_COLUMN_ORDER.forEach((key, i) => {
    if (map[key] === undefined || map[key] === -1) {
      map[key] = i;
    }
  });

  return map;
}

export async function parseWorkbook(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  if (!sheet) {
    throw new Error('Não foi possível ler a primeira planilha do arquivo.');
  }

  const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' });

  if (!allRows.length) {
    throw new Error('Planilha vazia.');
  }

  const [headers, ...rows] = allRows;
  return { headers, rows };
}

export function normalizeRow(rawRow, columnMap, rowNumber) {
  if (isRowEmpty(rawRow)) return null;

  const get = (key) => rawRow[columnMap[key]];

  const row = {
    rowIndex: rowNumber,
    tipoRaw: get('tipo'),
    statusRaw: get('status'),
    contaRaw: get('conta'),
    categoriaRaw: get('categoria'),
    description: get('descricao') !== undefined ? String(get('descricao')).trim() : '',
    conta2Raw: get('conta2'),
    dateCompetencia: parseDateBR(get('competencia')),
    dateVencimento: parseDateBR(get('vencimento')),
    juros: parseCurrencyBR(get('juros')),
    multa: parseCurrencyBR(get('multa')),
    desconto: parseCurrencyBR(get('desconto')),
    valor: parseCurrencyBR(get('valor')),
    journalEntryType: null,
    isInput: null,
    isTransferLeg: false,
    statusPayment: false,
    categoryId: null,
    bankAccountId: null,
    bankAccountId2: null,
    errors: [],
    warnings: [],
  };

  return validateNormalizedRow(row);
}

function validateNormalizedRow(row) {
  if (!row.dateCompetencia) row.errors.push('Data de competência inválida.');
  if (!row.dateVencimento) row.errors.push('Data de vencimento inválida.');
  if (Number.isNaN(row.juros)) row.errors.push('Juros inválido.');
  if (Number.isNaN(row.multa)) row.errors.push('Multa inválida.');
  if (Number.isNaN(row.desconto)) row.errors.push('Desconto inválido.');
  if (Number.isNaN(row.valor)) row.errors.push('Valor inválido.');
  return row;
}

export function classifyRow(row) {
  const tipoKey = normalizeText(row.tipoRaw);
  const classification = TIPO_MAP[tipoKey];

  if (!classification) {
    row.errors.push(`Tipo desconhecido: "${row.tipoRaw}"`);
  } else {
    Object.assign(row, classification);
  }

  const statusKey = normalizeText(row.statusRaw);
  if (statusKey === 'pago') {
    row.statusPayment = true;
  } else {
    row.statusPayment = false;
    if (!KNOWN_UNPAID_STATUS.includes(statusKey)) {
      row.warnings.push(`Status não reconhecido: "${row.statusRaw}" — tratado como não pago.`);
    }
  }

  return row;
}

const SAME_ACCOUNT_ERROR = 'Conta e Conta 2 não podem ser iguais.';

// Mesma regra já aplicada nos formulários manuais de Transferência (inputTransferValidation
// em helpers/formHelper.js, a !== b) — aqui precisa ser reavaliada tanto na resolução
// inicial quanto sempre que Conta/Conta 2 forem trocadas manualmente na revisão.
function refreshSameAccountError(row) {
  const isSameAccount =
    row.isTransferLeg && row.bankAccountId && row.bankAccountId2 && row.bankAccountId === row.bankAccountId2;

  if (isSameAccount) {
    addUniqueError(row, SAME_ACCOUNT_ERROR);
  } else {
    row.errors = row.errors.filter((e) => e !== SAME_ACCOUNT_ERROR);
  }

  return row;
}

export function resolveNames(rows, banks = [], categories = []) {
  rows.forEach((row) => {
    const bank = banks.find((b) => normalizeText(b.name) === normalizeText(row.contaRaw));
    if (!bank) {
      row.errors.push(`Conta não encontrada: "${row.contaRaw}"`);
      row.bankAccountId = null;
    } else {
      row.bankAccountId = bank.id;
    }

    if (row.isTransferLeg) {
      row.categoryId = null;

      if (!row.conta2Raw || !normalizeText(row.conta2Raw)) {
        row.errors.push('Conta 2 não informada.');
        row.bankAccountId2 = null;
      } else {
        const bank2 = banks.find((b) => normalizeText(b.name) === normalizeText(row.conta2Raw));
        if (!bank2) {
          row.errors.push(`Conta 2 não encontrada: "${row.conta2Raw}"`);
          row.bankAccountId2 = null;
        } else {
          row.bankAccountId2 = bank2.id;
        }
      }

      refreshSameAccountError(row);
      return;
    }

    if (!row.categoriaRaw || !normalizeText(row.categoriaRaw)) {
      row.errors.push('Categoria não informada.');
      row.categoryId = null;
      return;
    }

    const category = categories.find(
      (c) => c.isInput === row.isInput && normalizeText(c.name) === normalizeText(row.categoriaRaw)
    );

    if (!category) {
      row.errors.push(`Categoria não encontrada: "${row.categoriaRaw}"`);
      row.categoryId = null;
    } else {
      row.categoryId = category.id;
    }
  });

  return rows;
}

function generatePairKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `pair-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function addUniqueError(row, message) {
  if (!row.errors.includes(message)) row.errors.push(message);
}

export function applyBankAccountOverride(row, bank) {
  row.bankAccountId = bank.id;
  row.contaRaw = bank.name;
  row.errors = row.errors.filter((e) => !e.startsWith('Conta não encontrada'));
  return refreshSameAccountError(row);
}

export function clearBankAccountOverride(row) {
  row.bankAccountId = null;
  addUniqueError(row, `Conta não encontrada: "${row.contaRaw}"`);
  return refreshSameAccountError(row);
}

export function applyBankAccount2Override(row, bank) {
  row.bankAccountId2 = bank.id;
  row.conta2Raw = bank.name;
  row.errors = row.errors.filter((e) => !e.startsWith('Conta 2 não'));
  return refreshSameAccountError(row);
}

export function clearBankAccount2Override(row) {
  row.bankAccountId2 = null;
  addUniqueError(
    row,
    row.conta2Raw ? `Conta 2 não encontrada: "${row.conta2Raw}"` : 'Conta 2 não informada.'
  );
  return refreshSameAccountError(row);
}

export function applyCategoryOverride(row, category) {
  row.categoryId = category.id;
  row.categoriaRaw = category.name;
  row.errors = row.errors.filter(
    (e) => !e.startsWith('Categoria não encontrada') && e !== 'Categoria não informada.'
  );
  return row;
}

export function clearCategoryOverride(row) {
  row.categoryId = null;
  addUniqueError(
    row,
    row.categoriaRaw ? `Categoria não encontrada: "${row.categoriaRaw}"` : 'Categoria não informada.'
  );
  return row;
}

// Removes a row from the preview.
export function removeRow(rows, rowIndex) {
  return rows.filter((r) => r.rowIndex !== rowIndex);
}

export async function processSpreadsheet(file, banks, categories) {
  const { headers, rows } = await parseWorkbook(file);
  const columnMap = buildColumnMap(headers);

  const normalized = rows
    .map((rawRow, i) => normalizeRow(rawRow, columnMap, i + 2))
    .filter(Boolean);

  normalized.forEach(classifyRow);
  resolveNames(normalized, banks, categories);

  return normalized;
}

// Cada linha de transferência já carrega as duas contas envolvidas (Conta/Conta2), então
// vira DOIS lançamentos ligados por um transferPairKey gerado aqui — uma perna saindo de
// Conta, outra entrando em Conta2 (ou vice-versa, conforme a direção lida do Tipo).
export function buildEntryPayloads(rows, companyId, userId) {
  const payloads = [];

  rows
    .filter((r) => r.errors.length === 0)
    .forEach((r) => {
      const base = {
        companyId,
        userCreateId: userId,
        userUpdateId: userId,
        dateCompetencia: r.dateCompetencia,
        dateVencimento: r.dateVencimento,
        paymentType: 1,
        description: r.description || '',
        statusPayment: r.statusPayment,
        fineValue: r.multa || 0,
        interestValue: r.juros || 0,
        discountValue: r.desconto || 0,
        value: r.valor,
      };

      if (!r.isTransferLeg) {
        payloads.push({
          ...base,
          journalEntryType: r.journalEntryType,
          isInput: r.isInput,
          categoryId: r.categoryId,
          bankAccountId: r.bankAccountId,
        });
        return;
      }

      const transferPairKey = generatePairKey();
      payloads.push({
        ...base,
        journalEntryType: 5,
        isInput: r.isInput,
        categoryId: null,
        bankAccountId: r.bankAccountId,
        transferPairKey,
      });
      payloads.push({
        ...base,
        journalEntryType: 5,
        isInput: !r.isInput,
        categoryId: null,
        bankAccountId: r.bankAccountId2,
        transferPairKey,
      });
    });

  return payloads;
}

export function downloadImportTemplate() {
  const header = [
    'Tipo',
    'Status',
    'Competência',
    'Vencimento',
    'Conta',
    'Categoria',
    'Descrição',
    'Conta2',
    'Juros',
    'Multa',
    'Desconto',
    'Valor',
  ];

  // Duas linhas de exemplo (dados fictícios) demonstrando o formato esperado de cada
  // coluna — datas DD/MM/AAAA, valores no padrão "R$ 0,00" e "R$ -" para zero. Para
  // Transferência, Conta2 é obrigatória (a outra conta do movimento) e Categoria fica vazia.
  const examples = [
    [
      'Saída',
      'Pago',
      '05/08/2026',
      '10/08/2026',
      'Nome da conta cadastrada no Vita',
      'Nome da categoria cadastrada no Vita',
      'Exemplo de descrição do lançamento',
      '',
      'R$ -',
      'R$ -',
      'R$ -',
      'R$ 150,00',
    ],
    [
      'Transferência (saída)',
      'Pago',
      '05/08/2026',
      '05/08/2026',
      'Nome da conta de origem cadastrada no Vita',
      '',
      'Exemplo de transferência entre contas',
      'Nome da conta de destino cadastrada no Vita',
      'R$ -',
      'R$ -',
      'R$ -',
      'R$ 1.000,00',
    ],
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([header, ...examples]);
  XLSX.utils.book_append_sheet(wb, ws, 'Lançamentos');
  XLSX.writeFile(wb, 'modelo_importacao_lancamentos.xlsx');
}
